import cn from 'classnames';
import { FC, useState } from 'react';
import { AiOutlineCheck } from 'react-icons/ai';
import { Tooltip } from '../../features/tooltip/tooltip';
import { isMobile } from '../../helpers/devices';
import { useLang } from '../../hooks/use-lang';
import { useToggle } from '../../hooks/use-toggle';
import { IdValue } from '../../types/props';
import { Input, InputProps } from '../input/input';
import s from './select.module.css';

interface Props extends Omit<InputProps, 'value' | 'defaultValue' | 'onSelect'> {
  options: IdValue[];
  value?: IdValue;
  onSelect?: (value: IdValue) => void;
  position?: 'top' | 'bottom';
  inputClassname?: string;
  prefix?: string;
  withSearch?: boolean;
}

export const Select: FC<Props> = (props) => {
  const {
    options,
    value,
    className,
    disabled,
    size = 'lg',
    position = 'bottom',
    onSelect,
    inputClassname,
    tabIndex,
    name,
    prefix,
    placeholder,
    withSearch,
    ...other
  } = props;

  const { t } = useLang();
  const [open, toggleOpen, setOpen] = useToggle();
  const handleClose = () => setOpen(false);

  const optionsClassnames = cn(
    s.options,
    open && s.open,
    position === 'top' && s.top,
    isMobile() && s.mobile,
  );

  const [search, setSearch] = useState('');

  const filteredOptions = options.filter((item) =>
    item.value.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (e: IdValue) => {
    if (disabled) return;
    onSelect?.(e);
    handleClose();
  };

  return (
    <Tooltip
      withArrow
      triggerWrapperClassName={cn(s.component, className, s[size])}
      tabIndex={tabIndex}
      name={name}
      trigger={
        <Input
          readOnly
          value={prefix && value ? `${prefix}${value.value}` : value?.value}
          placeholder={prefix ? `${prefix}${placeholder}` : placeholder}
          className={cn(s.input, inputClassname)}
          disabled={disabled}
          tabIndex={tabIndex}
          name={name}
          {...other}
        />
      }
      disabled={disabled}
      open={open}
      onOpen={toggleOpen}
      optionsWrapperClassName={optionsClassnames}
    >
      <>
        {withSearch && (
          <Input
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder={t('Поиск')}
            className={s.search}
          />
        )}
        {filteredOptions.length ? (
          filteredOptions.map((item) => {
            const isSelected = value?.id === item.id;
            const isDisabled = Boolean(item.disabled);
            return (
              <li
                key={item.id}
                className={cn(s.option, isSelected && s.selected, isDisabled && s.optionDisabled)}
                onClick={() => !isDisabled && handleSelect(item)}
                aria-selected={isSelected}
                aria-disabled={isDisabled}
              >
                {isSelected && <AiOutlineCheck className={s.check} />}
                <span className={s.value}>{item.value}</span>
              </li>
            );
          })
        ) : (
          <li key="empty" className={s.empty}>
            {t('Здесь пока пусто')}
          </li>
        )}
      </>
    </Tooltip>
  );
};
